import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    makeStyles,
    Theme,
    Menu,
    MenuItem,
    useTheme,
    FormControl,
    Select,
    Switch,
    Typography,
    Button,
    Tooltip,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep'
import CheckIcon from '@material-ui/icons/Check'
import SearchIcon from '@material-ui/icons/Search'
import {
    TableHeaders,
    StartDateDESC,
    renderCellContent,
    getSortOptionLabels,
    getActiveSortLabel,
    TableHeader,
} from './ProjectTableCell'
import { usePopover } from '../../../hooks/usePopover'
import DetailsIcon from '@material-ui/icons/Details'
import { useUserTags } from '../../../contexts/UserTagsContext'
import { Chip } from '@material-ui/core'
import { Container } from '../../../components'
import { Pagination, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import {
    useProjectAnalytics,
    ProjectAnalytics,
} from '../../../hooks/useEZFolioProject'
import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../../../helpers/axios'
import { toast } from 'react-toastify'
import { useUser, useBlanket, useDebounce } from '../../../hooks'
import { typographyStyle } from '../../../theme/typographyStyle'
import { Blanket } from '../../../components/Blanket/Blanket'
import { ProjectAnalyticsCharts } from './ProjectAnalyticsCharts'
import { ProjectTagFilterSelect } from './ProjectTagFilterSelect'
const useStyles = makeStyles((theme: Theme) => ({
    table: {
        tableLayout: 'fixed',
        minWidth: '1200px', // Ensure table has minimum width
        '& th:first-child, & td:first-child': {
            width: '200px',
        },
        '& th, & td': {
            width: '90px',
        },
        '& th:last-child, & td:last-child': {
            width: '90px',
        },
        '& th:nth-last-child(2), & td:nth-last-child(2)': {
            width: '100px',
            padding: theme.spacing(0.5),
        },
    },
    tooltip: {
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
    },
    tableHeader: {
        '& th': {
            position: 'sticky',
            zIndex: 2,
        },
    },
    headerTitleRow: {
        '& th': {
            top: 0,
            zIndex: 3,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: '0.875rem',
            textAlign: 'center',
            verticalAlign: 'bottom',
            padding: theme.spacing(1, 0.5),
            whiteSpace: 'normal',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            borderBottom: 'none',
        },
    },
    headerSortToolbarRow: {
        '& th': {
            top: 'var(--analytics-title-row-height, 56px)',
            height: 36,
            zIndex: 2,
            padding: theme.spacing(0.25, 0.5),
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.text.primary,
            textAlign: 'center',
            verticalAlign: 'middle',
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
        },
    },
    sortButton: {
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        lineHeight: 1.2,
        padding: theme.spacing(0.25, 0.5),
        textTransform: 'none',
        maxWidth: '100%',
        minWidth: 0,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
    },
    sortButtonActive: {
        color: theme.palette.primary.main,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.primary.dark,
        },
    },
    sortButtonLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        width: '100%',
    },
    cell: {
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
        fontSize: '0.875rem',
        '& .MuiIconButton-root': {
            padding: theme.spacing(0.5),
            marginRight: theme.spacing(0.5),
            '&:last-child': {
                marginRight: 0,
            },
        },
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    customSwitch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.success.main,
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.success.main,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: theme.palette.error.main,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: theme.palette.error.main,
        },
    },
}))

interface Props {
    navigateToProject: (projectAnalytic: ProjectAnalytics) => void
    onArchiveProject: (projectId: number) => void
    onViewProjectDetails: (project: ProjectAnalytics) => void
    workspaceId?: number
    workspaceName?: string
    onResetWorkspace?: () => void
    showArchived: boolean
    setShowArchived: (showArchived: boolean) => void
    selectedTagIds?: number[]
    onSelectedTagIdsChange?: (tagIds: number[]) => void
    showAnalytics: boolean
    setShowAnalytics: React.Dispatch<React.SetStateAction<boolean>>
}

export const ProjectAnalyticsTable: React.FC<Props> = ({
    workspaceId,
    workspaceName,
    showArchived,
    navigateToProject,
    onArchiveProject,
    onViewProjectDetails,
    onResetWorkspace,
    setShowArchived,
    selectedTagIds = [],
    onSelectedTagIdsChange,
    showAnalytics,
    setShowAnalytics,
}) => {
    const classes = useStyles()
    const theme = useTheme()

    const { organization } = useUser()

    const tableContainerRef = useRef<HTMLDivElement>(null)
    const blanketRect = useBlanket(showAnalytics, tableContainerRef)
    const analyticsBlanketReady =
        showAnalytics && blanketRect.width > 0 && blanketRect.height > 0

    // Sorting state
    const [orderBy, setOrderBy] = useState<string>(StartDateDESC)
    const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(
        null,
    )
    const [sortMenuHeader, setSortMenuHeader] = useState<TableHeader | null>(
        null,
    )
    const sortMenuOpen = Boolean(sortMenuAnchor)
    const titleRowRef = useRef<HTMLTableRowElement>(null)
    const [titleRowHeight, setTitleRowHeight] = useState(56)
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 400)
    const tagIdsKey = selectedTagIds.join(',')

    const {
        projectAnalytics,
        projectAnalyticsLoading,
        refetchProjectAnalytics,
        totalCount,
        numPages,
        currentPage,
        start,
        end,
        pageSize,
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
        setWorkspaceId,
    } = useProjectAnalytics({
        limit: 10,
        offset: 0,
        sort: orderBy,
        workspaceId: workspaceId,
        archived: showArchived,
        organizationId: organization ?? undefined,
        search: debouncedSearch.trim() || undefined,
        tagIds: selectedTagIds,
    })

    const {
        data: allProjectAnalytics,
        isLoading: allProjectsLoading,
    } = useQuery<{
        data: { results: ProjectAnalytics[]; count: number }
    }>({
        queryKey: [
            'project-analytics-all',
            workspaceId,
            showArchived,
            organization,
            debouncedSearch.trim(),
            tagIdsKey,
        ],
        queryFn: () =>
            axiosInstance.get(
                '/workorder/org-project-analytics/project-analytics/',
                {
                    params: {
                        limit: 10000,
                        offset: 0,
                        sort: StartDateDESC,
                        ...(workspaceId && { workspace_id: workspaceId }),
                        ...(showArchived && { archived: showArchived }),
                        ...(organization && {
                            organization_id: organization,
                        }),
                        ...(debouncedSearch.trim() && {
                            search: debouncedSearch.trim(),
                        }),
                        ...(selectedTagIds.length > 0 && {
                            tag_ids: selectedTagIds.join(','),
                        }),
                    },
                },
            ),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: showAnalytics,
    })

    const { userTags } = useUserTags()

    // Helper function to render tags
    const renderTags = (project: ProjectAnalytics) => {
        const relevantTags = userTags.filter((tag) =>
            tag.apartments.includes(project.apartment_id),
        )

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {relevantTags.map((tag) => (
                    <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{
                            backgroundColor: tag.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px',
                        }}
                    />
                ))}
            </div>
        )
    }

    const allProjectsForCharts = useMemo(() => {
        return allProjectAnalytics?.data.results ?? []
    }, [allProjectAnalytics])

    const {
        isOpen,
        anchorEl,
        handleOpen,
        handleClose,
        context,
    } = usePopover<ProjectAnalytics>()
    const [viewMode, setViewMode] = useState<'serviceOrders' | 'budget'>(
        'serviceOrders',
    )

    const [
        selectedProject,
        setSelectedProject,
    ] = useState<ProjectAnalytics | null>(null)

    const [serviceLevelData, setServiceLevelData] = useState<
        ProjectAnalytics[] | null
    >(null)
    const [serviceLevelLoading, setServiceLevelLoading] = useState(false)

    const filteredTableHeaders = TableHeaders.filter(
        (header) => header.viewMode === 'all' || header.viewMode === viewMode,
    )

    useEffect(() => {
        const row = titleRowRef.current
        if (!row) return

        const measure = () => {
            setTitleRowHeight(row.getBoundingClientRect().height)
        }

        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(row)
        return () => observer.disconnect()
    }, [filteredTableHeaders, viewMode])

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        project: ProjectAnalytics,
    ) => {
        event.stopPropagation()
        handleOpen(event, project)
    }

    const handleMenuClose = () => {
        handleClose()
    }

    const handleNavigateClick = (project: ProjectAnalytics) => {
        handleClose()
        navigateToProject(project)
    }

    const handleArchiveClick = (project: ProjectAnalytics) => {
        handleClose()
        onArchiveProject(project.id)
    }

    const handleViewProjectDetailsClick = (project: ProjectAnalytics) => {
        handleClose()
        onViewProjectDetails(project)
    }

    const handleRowClick = async (project: ProjectAnalytics) => {
        if (project.id === selectedProject?.id) {
            setSelectedProject(null)
            return
        }
        setServiceLevelLoading(true)
        try {
            const response = await axiosInstance.get(
                `/workorder/org-project-analytics/service-level-analytics/`,
                {
                    params: { project_id: project.id },
                },
            )
            setServiceLevelData(response.data)
            if (response.data.length === 0) {
                toast.error('No services!')
            }
        } catch (error) {
            toast.error('Failed to fetch service level analytics')
        } finally {
            setServiceLevelLoading(false)
        }
        setSelectedProject(project)
    }

    const handleViewModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newViewMode: 'serviceOrders' | 'budget' | null,
    ) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode)
        }
    }

    const openSortMenu = (
        event: React.MouseEvent<HTMLElement>,
        header: TableHeader,
    ) => {
        event.stopPropagation()
        setSortMenuAnchor(event.currentTarget)
        setSortMenuHeader(header)
    }

    const closeSortMenu = () => {
        setSortMenuAnchor(null)
        setSortMenuHeader(null)
    }

    const applySort = (sortKey: string) => {
        setOrderBy(sortKey)
        closeSortMenu()
    }

    const sortMenuLabels =
        sortMenuHeader?.sortKind != null
            ? getSortOptionLabels(sortMenuHeader.sortKind)
            : null

    const renderHeaderSortButton = (header: TableHeader) => {
        const activeSortLabel = getActiveSortLabel(header, orderBy)
        const buttonLabel = activeSortLabel ?? 'Sort'
        return (
            <Tooltip
                title={activeSortLabel ?? `Sort by ${header.display}`}
                classes={{ tooltip: classes.tooltip }}
            >
                <Button
                    size="small"
                    fullWidth
                    variant="text"
                    disableRipple
                    className={`${classes.sortButton} ${
                        activeSortLabel ? classes.sortButtonActive : ''
                    }`}
                    aria-label={`Sort ${header.display}`}
                    aria-haspopup="true"
                    aria-expanded={
                        sortMenuOpen && sortMenuHeader?.field === header.field
                    }
                    onClick={(e) => openSortMenu(e, header)}
                >
                    <span className={classes.sortButtonLabel}>
                        {buttonLabel}
                    </span>
                </Button>
            </Tooltip>
        )
    }

    return (
        <Container
            ref={tableContainerRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            {showAnalytics && (
                <Blanket open rect={blanketRect}>
                    {analyticsBlanketReady && (
                        <ProjectAnalyticsCharts
                            key={`${blanketRect.width}x${blanketRect.height}-${viewMode}`}
                            projects={allProjectsForCharts}
                            viewMode={viewMode}
                            loading={allProjectsLoading}
                            onClose={() => setShowAnalytics(false)}
                        />
                    )}
                </Blanket>
            )}

            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.spacing(1),
                    marginRight: theme.spacing(1),
                }}
            >
                {workspaceName && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: theme.spacing(1),
                            alignItems: 'center',
                            marginRight: theme.spacing(2),
                        }}
                    >
                        <Typography>Projects for {workspaceName}</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            style={{
                                color: theme.palette.primary.main,
                                borderColor: theme.palette.primary.main,
                            }}
                            onClick={() => onResetWorkspace?.()}
                        >
                            <Typography>Show All Projects</Typography>
                        </Button>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                        marginLeft: 'auto',
                    }}
                >
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        style={{ minWidth: 220 }}
                    />
                    {onSelectedTagIdsChange && (
                        <ProjectTagFilterSelect
                            selectedTagIds={selectedTagIds}
                            onChange={onSelectedTagIdsChange}
                        />
                    )}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        style={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.grey[300]}`,
                        }}
                    >
                        <ToggleButton
                            value="serviceOrders"
                            aria-label="service orders"
                        >
                            Service Orders
                        </ToggleButton>
                        <ToggleButton value="budget" aria-label="budget">
                            Budget
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <Typography>Active</Typography>
                    <Switch
                        checked={showArchived}
                        onChange={() => setShowArchived(!showArchived)}
                        className={classes.customSwitch}
                    />
                    <Typography>Archived</Typography>
                </div>
            </Container>

            {/* Table */}
            <Container
                style={{
                    overflowX: 'auto',
                    width: '100%',
                }}
            >
                <Table
                    className={classes.table}
                    stickyHeader
                    style={
                        {
                            '--analytics-title-row-height': `${titleRowHeight}px`,
                        } as React.CSSProperties
                    }
                >
                    <TableHead className={classes.tableHeader}>
                        <TableRow
                            ref={titleRowRef}
                            className={classes.headerTitleRow}
                        >
                            {filteredTableHeaders.map((header) => (
                                <React.Fragment key={header.field}>
                                    <Tooltip
                                        title={header.tooltip}
                                        classes={{ tooltip: classes.tooltip }}
                                    >
                                        <TableCell
                                            component="th"
                                            style={{ width: header.minWidth }}
                                        >
                                            {header.display}
                                        </TableCell>
                                    </Tooltip>
                                    {header.field === 'apartment_name' && (
                                        <TableCell
                                            component="th"
                                            style={{ width: 120 }}
                                        >
                                            Tag
                                        </TableCell>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableRow>
                        <TableRow className={classes.headerSortToolbarRow}>
                            {filteredTableHeaders.map((header) => (
                                <React.Fragment key={`sort-${header.field}`}>
                                    <TableCell
                                        component="th"
                                        style={{ width: header.minWidth }}
                                    >
                                        {header.sortAsc !== null &&
                                            header.sortDesc !== null &&
                                            header.sortKind != null &&
                                            renderHeaderSortButton(header)}
                                    </TableCell>
                                    {header.field === 'apartment_name' && (
                                        <TableCell
                                            component="th"
                                            style={{ width: 120 }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>

                    {projectAnalyticsLoading ? (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    colSpan={filteredTableHeaders.length}
                                >
                                    Loading...
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    ) : (
                        <>
                            <TableBody>
                                {projectAnalytics?.data.results.map(
                                    (project) => (
                                        <React.Fragment key={project.id}>
                                            <TableRow
                                                key={project.id}
                                                onClick={() =>
                                                    handleRowClick(project)
                                                }
                                                className={classes.row}
                                            >
                                                {filteredTableHeaders.map(
                                                    (header) => {
                                                        return (
                                                            <React.Fragment
                                                                key={
                                                                    header.field
                                                                }
                                                            >
                                                                {header.field ===
                                                                'apartment_name' ? (
                                                                    <>
                                                                        <TableCell
                                                                            style={{
                                                                                textAlign:
                                                                                    'left',
                                                                            }}
                                                                            title={
                                                                                project.apartment_name
                                                                            }
                                                                        >
                                                                            {
                                                                                project.apartment_name
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell
                                                                            style={{
                                                                                textAlign:
                                                                                    'left',
                                                                            }}
                                                                        >
                                                                            {renderTags(
                                                                                project,
                                                                            )}
                                                                        </TableCell>
                                                                    </>
                                                                ) : (
                                                                    renderCellContent(
                                                                        header,
                                                                        project,
                                                                        classes,
                                                                        handleMenuOpen,
                                                                        theme,
                                                                    )
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    },
                                                )}
                                            </TableRow>
                                            {selectedProject &&
                                                selectedProject.id ===
                                                    project.id &&
                                                !serviceLevelLoading &&
                                                serviceLevelData &&
                                                serviceLevelData.map(
                                                    (service) => (
                                                        <TableRow
                                                            key={service.id}
                                                            style={{
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .grey[200],
                                                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                                            }}
                                                        >
                                                            {filteredTableHeaders.map(
                                                                (header) => {
                                                                    return (
                                                                        <React.Fragment
                                                                            key={
                                                                                header.field
                                                                            }
                                                                        >
                                                                            {header.field ===
                                                                            'apartment_name' ? (
                                                                                <>
                                                                                    <TableCell
                                                                                        style={{
                                                                                            textAlign:
                                                                                                'left',
                                                                                        }}
                                                                                        title={
                                                                                            service.apartment_name
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            service.apartment_name
                                                                                        }
                                                                                    </TableCell>
                                                                                    <TableCell
                                                                                        style={{
                                                                                            textAlign:
                                                                                                'left',
                                                                                        }}
                                                                                    >
                                                                                        {renderTags(
                                                                                            service,
                                                                                        )}
                                                                                    </TableCell>
                                                                                </>
                                                                            ) : (header.field as string) ===
                                                                              'tags' ? (
                                                                                <TableCell
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'left',
                                                                                    }}
                                                                                >
                                                                                    {renderTags(
                                                                                        service,
                                                                                    )}
                                                                                </TableCell>
                                                                            ) : (
                                                                                renderCellContent(
                                                                                    header,
                                                                                    service,
                                                                                    classes,
                                                                                    handleMenuOpen,
                                                                                    theme,
                                                                                    true,
                                                                                )
                                                                            )}
                                                                        </React.Fragment>
                                                                    )
                                                                },
                                                            )}
                                                        </TableRow>
                                                    ),
                                                )}
                                        </React.Fragment>
                                    ),
                                )}
                            </TableBody>

                            <Menu
                                anchorEl={anchorEl}
                                open={isOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem
                                    onClick={() =>
                                        context && handleNavigateClick(context)
                                    }
                                >
                                    <ExitToAppIcon
                                        fontSize="small"
                                        style={{ marginRight: 8 }}
                                    />
                                    Go to Project
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        context &&
                                        handleViewProjectDetailsClick(context)
                                    }
                                >
                                    <DetailsIcon
                                        fontSize="small"
                                        style={{ marginRight: 8 }}
                                    />
                                    View Project Details
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        context && handleArchiveClick(context)
                                    }
                                >
                                    <DeleteSweepIcon
                                        fontSize="small"
                                        style={{ marginRight: 8 }}
                                    />
                                    Archive Project
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Table>

                <Menu
                    anchorEl={sortMenuAnchor}
                    open={sortMenuOpen}
                    onClose={closeSortMenu}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    {sortMenuHeader &&
                        sortMenuLabels &&
                        sortMenuHeader.sortDesc &&
                        sortMenuHeader.sortAsc && (
                            <>
                                <MenuItem
                                    selected={
                                        orderBy === sortMenuHeader.sortDesc
                                    }
                                    onClick={() =>
                                        applySort(sortMenuHeader.sortDesc!)
                                    }
                                >
                                    {orderBy === sortMenuHeader.sortDesc && (
                                        <CheckIcon
                                            fontSize="small"
                                            style={{ marginRight: 8 }}
                                        />
                                    )}
                                    {sortMenuLabels.high}
                                </MenuItem>
                                <MenuItem
                                    selected={
                                        orderBy === sortMenuHeader.sortAsc
                                    }
                                    onClick={() =>
                                        applySort(sortMenuHeader.sortAsc!)
                                    }
                                >
                                    {orderBy === sortMenuHeader.sortAsc && (
                                        <CheckIcon
                                            fontSize="small"
                                            style={{ marginRight: 8 }}
                                        />
                                    )}
                                    {sortMenuLabels.low}
                                </MenuItem>
                            </>
                        )}
                </Menu>
            </Container>

            {/* Pagination Controls */}
            <Container
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    padding: theme.spacing(1),
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    minHeight: '56px',
                    backgroundColor: theme.palette.background.paper,
                    position: 'sticky',
                    bottom: 0,
                }}
            >
                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(1) }}
                >
                    <span>Rows per page:</span>
                    <FormControl size="small" style={{ minWidth: 80 }}>
                        <Select
                            value={pageSize}
                            onChange={(e) =>
                                changePageSize(e.target.value as number)
                            }
                            displayEmpty
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </Container>

                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(2) }}
                >
                    <span>
                        {start} - {end} of {totalCount}
                    </span>
                    <Pagination
                        count={numPages}
                        page={currentPage}
                        onChange={(_: unknown, newPage: number) => {
                            goToPage(newPage)
                        }}
                        size="small"
                    />
                </Container>
            </Container>
        </Container>
    )
}
